import * as React from 'react';
import { CheckCircle2, XCircle, AlertCircle, Lightbulb } from 'lucide-react';
import { cn } from '../../lib/utils';
import { kitT, type KitTranslateFn } from '../../lib/kitTranslate';

export type FeedbackType = 'correct' | 'incorrect' | 'partial' | 'hint' | 'explanation';

export interface FeedbackCardProps {
    type: FeedbackType;
    title?: string;
    children: React.ReactNode;
    className?: string;
    translate?: KitTranslateFn;
}

const FEEDBACK_TITLE_KEYS: Record<FeedbackType, string> = {
    correct: 'kFcTitleCorrect',
    incorrect: 'kFcTitleIncorrect',
    partial: 'kFcTitlePartial',
    hint: 'kFcTitleHint',
    explanation: 'kFcTitleExplanation',
};

const FEEDBACK_TITLE_FALLBACKS: Record<FeedbackType, string> = {
    correct: 'Correct!',
    incorrect: 'Incorrect',
    partial: 'Partially Correct',
    hint: 'Hint',
    explanation: 'Explanation',
};

const feedbackConfig: Record<
    FeedbackType,
    {
        icon: React.ElementType;
        bgClass: string;
        borderClass: string;
        iconClass: string;
        titleClass: string;
    }
> = {
    correct: {
        icon: CheckCircle2,
        bgClass: 'bg-success/10',
        borderClass: 'border-success/30',
        iconClass: 'text-success',
        titleClass: 'text-success',
    },
    incorrect: {
        icon: XCircle,
        bgClass: 'bg-destructive/10',
        borderClass: 'border-destructive/30',
        iconClass: 'text-destructive',
        titleClass: 'text-destructive',
    },
    partial: {
        icon: AlertCircle,
        bgClass: 'bg-warning/10',
        borderClass: 'border-warning/30',
        iconClass: 'text-warning',
        titleClass: 'text-warning',
    },
    hint: {
        icon: Lightbulb,
        bgClass: 'bg-secondary/10',
        borderClass: 'border-secondary/30',
        iconClass: 'text-secondary',
        titleClass: 'text-secondary-foreground',
    },
    explanation: {
        icon: Lightbulb,
        bgClass: 'bg-primary/5',
        borderClass: 'border-primary/20',
        iconClass: 'text-primary',
        titleClass: 'text-foreground',
    },
};

export const FeedbackCard: React.FC<FeedbackCardProps> = ({
    type,
    title,
    children,
    className,
    translate,
}) => {
    const config = feedbackConfig[type];
    const Icon = config.icon;
    const defaultTitle = kitT(translate, FEEDBACK_TITLE_KEYS[type], FEEDBACK_TITLE_FALLBACKS[type]);

    return (
        <div
            role="alert"
            aria-live="polite"
            className={cn(
                // EAA-1676: BEM anchor classes for the playful skin's
                // pop/shake micro-animations (skin-playful.css).
                'content-feedback',
                `content-feedback--${type}`,
                'rounded-2xl border-2 p-5 animate-slide-up',
                config.bgClass,
                config.borderClass,
                className
            )}
        >
            <div className="flex items-start gap-4">
                <div
                    className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                        config.bgClass
                    )}
                >
                    <Icon className={cn('h-6 w-6', config.iconClass)} />
                </div>
                <div className="flex-1 space-y-1">
                    <h4 className={cn('text-lg font-semibold font-heading', config.titleClass)}>
                        {title || defaultTitle}
                    </h4>
                    <div className="text-base text-foreground/80 font-body">{children}</div>
                </div>
            </div>
        </div>
    );
};

FeedbackCard.displayName = 'FeedbackCard';
